import { DirectionalLight } from "@babylonjs/core/Lights/directionalLight";
import { ShadowGenerator as BJSShadowGenerator } from "@babylonjs/core/Lights/Shadows/shadowGenerator";
import { Quaternion, Vector2, Vector3 } from "@babylonjs/core/Maths/math.vector";
import { InstancedMesh } from "@babylonjs/core/Meshes/instancedMesh";
import { LinesMesh } from "@babylonjs/core/Meshes/linesMesh";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import "@babylonjs/core/Lights/Shadows/shadowGeneratorSceneComponent"; //side effect for ShadowGenerator
import { degToRad } from "../../utils/math";
import { PbrMaterial } from "../mdl/PbrMaterial";
import { rotateVector3ToRef } from "../utils/MathUtils";
import { removeFromScene } from "../utils/MeshUtils";
import { ShadowGenerator } from "./ShadowGenerator";
var ShadowMapping = /** @class */ (function () {
    /**
     * Creates a ShadowMapping instance. Only a single instance should be used at runtime.
     * @param pResolution Resolution of the shadw map. Can be increased for bigger scenes. (default: 2048)
     */
    function ShadowMapping(pApp, pResolution) {
        var _this = this;
        if (pResolution === void 0) { pResolution = 2048; }
        this.onModelModified = function () {
            _this.mShadowsNeedUpdate = true;
        };
        this.onViewerBeforeRender = function () {
            _this.updateShadows();
        };
        this.onCameraBeforeRender = function () {
            var tLight = _this.mShadowGenerator.getLight();
            var tZRangeFactor = Math.sqrt(tLight.shadowMaxZ - tLight.shadowMinZ);
            _this.mShadowGenerator.bias = 0.009 / tZRangeFactor;
            _this.mShadowGenerator.normalBias = 0.0015 * tZRangeFactor;
        };
        this.mApp = pApp;
        this.mScene = pApp.scene;
        this.mShadowsNeedUpdate = false;
        this.mSoftness = 0.1;
        this.mAltitude = 65;
        this.mAzimuth = -50;
        this.createShadowGenerator(pResolution);
        //register events
        this.mApp.model.eventModified.addListener(this.onModelModified);
        this.mApp.viewer.eventBeforeRender.addListener(this.onViewerBeforeRender);
        this.mApp.scene.onBeforeCameraRenderObservable.add(this.onCameraBeforeRender);
        //prepare materials
        PbrMaterial.DisableLighting = false;
    }
    Object.defineProperty(ShadowMapping.prototype, "enabled", {
        get: function () {
            return (this.mShadowGenerator.getLight().shadowEnabled);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(ShadowMapping.prototype, "softness", {
        /**
         * Defines the softness of the shadows.
         * @range [0, 0.5]
         * @defaulValue 0.1
         */
        get: function () {
            return (this.mSoftness);
        },
        set: function (pValue) {
            this.mSoftness = pValue;
            this.mApp.viewer.requestRenderFrame();
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(ShadowMapping.prototype, "altitude", {
        /**
         * Altitude of the shadow light in degree.
         * @range [0, 90]
         * @defaulValue 65
         */
        get: function () {
            return (this.mAltitude);
        },
        set: function (pValue) {
            this.mAltitude = pValue;
            this.mApp.viewer.requestRenderFrame();
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(ShadowMapping.prototype, "azimuth", {
        /**
         * Azimuth of the shadow light in degree.
         * @range [-360, 360]
         * @defaulValue -50
         */
        get: function () {
            return (this.mAzimuth);
        },
        set: function (pValue) {
            this.mAzimuth = pValue;
            this.mApp.viewer.requestRenderFrame();
        },
        enumerable: false,
        configurable: true
    });
    ShadowMapping.prototype.dispose = function () {
        this.mApp.model.eventModified.removeListener(this.onModelModified);
        this.mApp.viewer.eventBeforeRender.removeListener(this.onViewerBeforeRender);
        this.mApp.scene.onBeforeCameraRenderObservable.removeCallback(this.onCameraBeforeRender);
        this.mShadowsNeedUpdate = false;
    };
    ShadowMapping.prototype.setEnabled = function (pEnabled) {
        this.mShadowGenerator.getLight().shadowEnabled = pEnabled;
        //force update of frozen materials
        for (var _i = 0, _a = this.mScene.materials; _i < _a.length; _i++) {
            var tMat = _a[_i];
            if (tMat.isFrozen) {
                tMat.markDirty();
            }
        }
        this.mApp.viewer.requestRenderFrame();
    };
    ShadowMapping.prototype.notifyModelInteraction = function () {
    };
    ShadowMapping.prototype.prepareRenderFrame = function (pMultiSamplingStep, pMaxSteps) {
        var tIndex = pMultiSamplingStep >= 0 ? pMultiSamplingStep : 0;
        var tOffset = ShadowMapping.sShadowSamplingOffsets[tIndex];
        var tDir = new Vector3(tOffset.x * this.mSoftness, -1, tOffset.y * this.mSoftness);
        tDir.normalize();
        var tQuat = Quaternion.FromEulerAngles(0, degToRad(this.mAzimuth), degToRad(90 - this.mAltitude));
        rotateVector3ToRef(tDir, tQuat, tDir);
        this.mShadowGenerator.getLight().direction.copyFrom(tDir);
    };
    ShadowMapping.prototype.finalizeRenderFrame = function (pMultiSamplingStep, pMaxSteps) {
    };
    ShadowMapping.prototype.createShadowGenerator = function (pResolution) {
        var tLight = new DirectionalLight("shadow light", new Vector3(10, -30, 10).normalize(), this.mScene);
        tLight.intensity = 1.0;
        tLight.autoCalcShadowZBounds = true;
        tLight.shadowMinZ = 1.0;
        tLight.shadowMaxZ = 100.0;
        this.mShadowGenerator = new BJSShadowGenerator(pResolution, tLight);
        this.mShadowGenerator.usePoissonSampling = true;
        this.mShadowGenerator.transparencyShadow = true;
        this.mShadowGenerator.enableSoftTransparentShadow = true;
        this.mShadowGenerator.bias = 0.009;
        this.mShadowGenerator.normalBias = 0.0015;
        //dummy mesh is used to ensure that the render target will update
        //if a render target has an empty renderList it will not render
        this.mDummyMesh = new Mesh("shadowMapDummy", this.mScene);
        this.mDummyMesh.freezeWorldMatrix();
        this.mDummyMesh.isPickable = false;
        this.mDummyMesh.isVisible = false;
        this.mDummyMesh.setEnabled(false);
        removeFromScene(this.mDummyMesh, this.mScene);
    };
    ShadowMapping.prototype.addMeshToRenderList = function (pMesh, pRenderList) {
        if (pMesh == null || pMesh instanceof LinesMesh || ShadowGenerator.isMeshShadowDisabled(pMesh))
            return;
        if (pMesh instanceof Mesh && pMesh.geometry != null || pMesh instanceof InstancedMesh) {
            pRenderList.push(pMesh);
            if (pMesh instanceof Mesh) {
                pMesh.receiveShadows = true;
            }
        }
    };
    ;
    ShadowMapping.prototype.updateShadows = function () {
        if (!this.mShadowsNeedUpdate)
            return;
        this.mShadowsNeedUpdate = false;
        var tMap = this.mShadowGenerator.getShadowMap();
        if (tMap == null)
            return;
        //update renderlist
        tMap.renderList = [];
        tMap.renderList.push(this.mDummyMesh);
        for (var _i = 0, _a = this.mApp.model.elements; _i < _a.length; _i++) {
            var tElement = _a[_i];
            if (tElement.node != null) {
                if (tElement.node instanceof Mesh) {
                    this.addMeshToRenderList(tElement.node, tMap.renderList);
                }
                var tChilds = tElement.node.getChildMeshes(false);
                for (var _b = 0, tChilds_1 = tChilds; _b < tChilds_1.length; _b++) {
                    var tMesh = tChilds_1[_b];
                    this.addMeshToRenderList(tMesh, tMap.renderList);
                }
            }
        }
    };
    //// private ///////////////////////////////////////////////////////////////////////////////
    ShadowMapping.sShadowSamplingOffsets = [
        new Vector2(0, 0),
        new Vector2(0.1, 0.1),
        new Vector2(0.2, -0.1),
        new Vector2(0.2, 0.3),
        new Vector2(-0.1, -0.2),
        new Vector2(-0.2, 0.2),
        new Vector2(-0.3, -0.3),
        new Vector2(0.4, 0.1),
        new Vector2(0.4, -0.4),
        new Vector2(0.0, -0.4),
        new Vector2(-0.4, 0),
        new Vector2(-0.1, 0.4),
        new Vector2(-0.5, 0.4),
        new Vector2(0.4, 0.4),
        new Vector2(0.2, -0.5),
        new Vector2(0.6, 0.5),
        new Vector2(-0.3, 0.5),
        new Vector2(-0.3, -0.6),
        new Vector2(-0.6, -0.3),
        new Vector2(0.6, -0.2),
        new Vector2(0.5, -0.6),
        new Vector2(0.1, 0.6),
        new Vector2(-0.6, 0.1),
        new Vector2(-0.1, -0.7),
        new Vector2(-0.5, -0.7),
        new Vector2(0.5, 0.7),
        new Vector2(0.7, 0.2),
        new Vector2(0.7, -0.5),
        new Vector2(-0.7, -0.6),
        new Vector2(-0.7, -0.1),
        new Vector2(-0.7, 0.5),
        new Vector2(0.3, -0.7),
        new Vector2(-0.4, 0.7),
    ];
    return ShadowMapping;
}());
export { ShadowMapping };
